import { useMemo } from 'react';

import { Box, Icon, Tag, TextButton } from '@zillow/constellation-10';
import { IconCalendarClockFilled, IconDollarSignCircleFilled } from '@zillow/constellation-icons';

import { LoadingParagraph, LoadingTag } from '@zg-rentals/loading-components';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import { useRentalManagerUserPropertySummaryWebApiHandler_payments } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import numberUtils from '../../../../../srcLegacy/shared/utils/numberUtils';
import selectPropertyUtils from '../../../../../srcLegacy/shared/utils/selectPropertyUtils';
import { getTagTypeByKey, STATUS_KEY } from '../../../../../srcLegacy/shared/utils/statusTagUtils';

import { PAYMENT_STATUS_TEXT_TO_TYPE } from '../../../../../srcLegacy/shared/constants/PaymentConstants';
import SelectPropertyConstants from '../../../../../srcLegacy/shared/constants/SelectPropertyConstants';
import TimeConstants from '../../../../../srcLegacy/shared/constants/TimeConstants';

import ConnectedModuleCard from '../../../../../srcLegacy/shared/components/ConnectedModuleCard';
import EmptyState from '../../../../../srcLegacy/shared/components/EmptyState';
import ErrorMessagesAlert from '../../../../../srcLegacy/shared/components/ErrorMessagesAlert';
import SimpleCard from '../../../../../srcLegacy/shared/components/SimpleCard';

const { PAYMENT_SUMMARY } = STATUS_KEY;

type Props = {
  listingAlias: string;
};

const PaymentsModule = ({ listingAlias }: Props) => {
  const {
    data = {
      enableFastPayments: false,
      paymentCardList: [{ title: '', amount: 0, paymentTracker: { statusText: '' } }],
    },
    isPending,
    error,
  } = useRentalManagerUserPropertySummaryWebApiHandler_payments(
    { listingAlias },
    { enabled: Boolean(listingAlias), staleTime: TimeConstants.SECONDS_10 },
  );

  const paymentsPath = selectPropertyUtils.getPathByType(
    SelectPropertyConstants.PAYMENTS as keyof typeof SelectPropertyConstants,
    listingAlias,
  );

  const paymentCardListLength = data?.paymentCardList?.length || 0;

  const renderCTA = useMemo(() => {
    return (
      <TextButton tone="brand" asChild>
        <a href={paymentsPath || ''} target="_self">
          Details
        </a>
      </TextButton>
    );
  }, [paymentsPath]);

  const renderPaymentCardList = (paymentCardItem: {
    title: string;
    amount: number;
    paymentTracker: { statusText: string };
  }) => {
    const {
      title,
      amount,
      paymentTracker: { statusText },
    } = paymentCardItem;
    const displayAmount = numberUtils.asCurrency(amount / 100);

    const toneTypeKey = PAYMENT_STATUS_TEXT_TO_TYPE[statusText.toLowerCase()] || 'neutral';
    const tone = getTagTypeByKey(PAYMENT_SUMMARY, toneTypeKey);

    return (
      <Box key={`PaymentCard-${title}`}>
        <SimpleCard withChevron={false}>
          <LoadingTag
            data-testid="PaymentCard-Status"
            size="sm"
            tone={tone}
            css={{ margin: '0 {spacing.200} {spacing.200} 0' }}
            isLoading={isPending}
            loadingWidth="10%"
          >
            {statusText}
          </LoadingTag>
          <LoadingParagraph
            data-testid="PaymentCard-Title"
            textStyle="body-sm-bold"
            isLoading={isPending}
            loadingWidth="40%"
            css={{ fontSize: { lg: 'body' }, marginBottom: '200' }}
          >
            {title}
          </LoadingParagraph>
          <LoadingParagraph
            data-testid="PaymentCard-Amount"
            textStyle="body-sm"
            isLoading={isPending}
            loadingWidth="20%"
          >
            {displayAmount}
          </LoadingParagraph>
        </SimpleCard>
      </Box>
    );
  };

  return (
    <ConnectedModuleCard
      data-testid="PaymentsModule"
      title="Payments"
      ctaComponent={!error && renderCTA}
      ctaFontType="body-sm"
      justifyHeadingContent={'center'}
    >
      {error ? (
        <ErrorMessagesAlert
          appearance="warning"
          data-testid="PaymentsModuleError"
          errorMessages={
            DatarouterError.getErrorDetails(error)?.partialMessages || [
              {
                text:
                  DatarouterError.getErrorDetails(error)?.message ||
                  'Unable to load payments. Please refresh the page to try again.',
              },
            ]
          }
        />
      ) : (
        <>
          {data?.enableFastPayments && paymentCardListLength > 0 && (
            <Tag size="md" icon={<IconCalendarClockFilled />} css={{ marginBottom: '200' }}>
              New: Fast payments activated
            </Tag>
          )}

          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: {
                base: 'repeat(1, 1fr)',
                lg: paymentCardListLength < 2 ? 'repeat(1, minmax(0,1fr))' : 'repeat(2, minmax(0,1fr))',
              },
              gap: { base: '200', lg: '400' },
            }}
          >
            {paymentCardListLength > 0 ? (
              data?.paymentCardList.map(renderPaymentCardList)
            ) : (
              <EmptyState
                data-testid="PaymentsModuleEmpty"
                icon={<Icon size="md" css={{ color: 'icon.subtle' }} render={<IconDollarSignCircleFilled />} />}
                heading="Upcoming payments will appear here"
                headingLevel={4}
                body="Set up payments"
              />
            )}
          </Box>
        </>
      )}
    </ConnectedModuleCard>
  );
};

export default PaymentsModule;
